import React, { useRef } from 'react';

import type { CropModalProps } from '../types';
import { CropHeader } from './CropHeader';
import { CropInfoBar } from './CropInfoBar';
import { CropAspectControls } from './CropAspectControls';
import { CropPreview } from './CropPreview';
import { CropControls } from './CropControls';
import { CropImageRenderer } from './CropImageRenderer';
import { CropError } from './CropError';
import { useCropStore } from '../store/cropStore';
import { useImageLoader } from '../hooks/useImageLoader';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
import { useCropInitializer } from '../hooks/useCropInitializer';
import { modalOverlay, modalContainer, contentContainer, footerContainer } from '../styles/index';

export const CropModal: React.FC<CropModalProps> = ({
  imageUrl,
  onClose,
  onCrop,
  previewUrl,
  isLoading = false,
  aspectRatio: initialAspectRatio,
  labels = {},
}) => {
  // Refs
  const imgRef = useRef<HTMLImageElement>(null);

  // Store state
  const {
    isImageLoaded,
    setImageLoaded,
    cropPosition,
    setCropPosition,
    zoom,
    setZoom,
    croppedArea,
    setCroppedArea,
    aspectRatioValue,
    isSubmitting,
    setSubmitting,
    getCropData,
    error,
    setError,
  } = useCropStore();

  // Extract labels with defaults
  const {
    title = 'Crop Image',
    cancel = 'Cancel',
    apply = 'Apply Crop',
    loadingImage = 'Loading image...',
    original = 'Original',
    selection = 'Selection',
    aspectRatios = {},
    zoomTip = 'Use scroll wheel to zoom or pinch gestures on mobile',
  } = labels;

  // Custom hooks
  useImageLoader(imageUrl, initialAspectRatio);
  useKeyboardShortcuts(onClose, isSubmitting, isLoading);
  useCropInitializer(imgRef);

  // Handle image load
  const handleImageLoad = (): void => {
    if (imgRef.current) {
      setImageLoaded(true);
    }
  };

  // Handle applying crop
  const handleApplyCrop = async (): Promise<void> => {
    try {
      setSubmitting(true);
      setError(null); // Clear any previous errors
      await onCrop(getCropData());
      onClose();
    } catch (error) {
      console.error('Error applying crop:', error);
      setError(error instanceof Error ? error.message : 'Failed to apply crop. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div className={modalOverlay}>
      <div className={modalContainer}>
        {/* Header */}
        <CropHeader onClose={onClose} isLoading={isLoading} title={title} />

        {/* Error display */}
        <CropError error={error} onDismiss={() => setError(null)} />

        {/* Aspect ratio controls */}
        <CropAspectControls aspectRatioLabels={aspectRatios} />

        {/* Main content area */}
        <div className={contentContainer}>
          <CropImageRenderer
            isImageLoaded={isImageLoaded}
            cropPosition={cropPosition}
            zoom={zoom}
            croppedArea={croppedArea}
            aspectRatioValue={aspectRatioValue}
            imageUrl={imageUrl}
            imgRef={imgRef}
            onCropChange={setCropPosition}
            onZoomChange={setZoom}
            onCroppedAreaChange={setCroppedArea}
            onImageLoad={handleImageLoad}
            loadingMessage={loadingImage}
          />

          {/* Preview panel */}
          {previewUrl && <CropPreview imageUrl={imageUrl} />}
        </div>

        {/* Info bar - shows crop dimensions */}
        <CropInfoBar originalLabel={original} selectionLabel={selection} />

        {/* Footer controls */}
        <div className={footerContainer}>
          <CropControls
            onClose={onClose}
            onApply={handleApplyCrop}
            isLoading={isLoading}
            cancelLabel={cancel}
            applyLabel={apply}
            zoomTipLabel={zoomTip}
          />
        </div>
      </div>
    </div>
  );
};
